import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

const CONTENT_CLS =
  'font-body text-base leading-relaxed text-[var(--vibe-color-text-muted)]' +
  ' [&_h1]:font-heading [&_h1]:text-xl sm:[&_h1]:text-2xl [&_h1]:font-normal' +
  ' [&_h1]:text-[var(--vibe-color-text)] [&_h1]:mb-4 [&_h1]:italic [&_h1]:mt-10' +
  ' [&_h1:first-child]:mt-0' +
  ' [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_li]:mb-1' +
  ' [&_a]:text-primary [&_a]:underline';

const PROPERTY_NAME =
  '<span data-type="field-token" data-field="property_name"></span>';

const DEFAULT_BODY =
  `<h1>Terms &amp; Conditions of Use for SMS Messaging</h1>` +
  `<p>The terms and conditions described herein apply to all SMS messages sent by ${PROPERTY_NAME}. Use of this service constitutes consent to the terms and conditions outlined below.</p>` +
  `<h1>Property Name</h1>` +
  `<p>${PROPERTY_NAME}</p>` +
  `<h1>Messaging Consent</h1>` +
  `<p>A user must explicitly consent to receive SMS messages from ${PROPERTY_NAME}. Consent may be obtained through website forms, leasing inquiries, paper forms, or in-person interactions.</p>` +
  `<h1>Message Type and Frequency</h1>` +
  `<p>Users may receive messages related to leasing updates, appointment reminders, community announcements, and other property-related communications.</p>` +
  `<p><strong>Message frequency may vary</strong> depending on the user&rsquo;s engagement and communication preferences.</p>` +
  `<h1>Data Rates</h1>` +
  `<p>Standard message and data rates may apply. Users are responsible for any charges incurred from their mobile carrier.</p>` +
  `<h1>Customer Care</h1>` +
  `<p>Text &quot;HELP&quot; for help</p>` +
  `<h1>Opt-Out</h1>` +
  `<p>To stop receiving messages, reply &ldquo;STOP&rdquo; at any time to unsubscribe.</p>` +
  `<h1>Privacy</h1>` +
  `<p>Your information is handled in accordance with our <a href="/privacy-policy">Privacy Policy</a>.</p>`;

export function TermsAndConditionsContent() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up py-16 md:py-24 bg-[var(--vibe-color-background)]"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <RichTextContent
          path="termsandconditionscontent.body"
          defaultValue={DEFAULT_BODY}
          as="div"
          className={CONTENT_CLS}
        />
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'TermsAndConditionsContent',
  displayName: 'Terms and Conditions Content',
  tokens: {
    'termsandconditionscontent.body': {
      label: 'Terms and Conditions Content',
      type: 'richtext',
      defaultValue:
        `<h1>Terms &amp; Conditions of Use for SMS Messaging</h1>` +
        `<p>The terms and conditions described herein apply to all SMS messages sent by <span data-type="field-token" data-field="property_name"></span>. Use of this service constitutes consent to the terms and conditions outlined below.</p>` +
        `<h1>Property Name</h1>` +
        `<p><span data-type="field-token" data-field="property_name"></span></p>` +
        `<h1>Messaging Consent</h1>` +
        `<p>A user must explicitly consent to receive SMS messages from <span data-type="field-token" data-field="property_name"></span>. Consent may be obtained through website forms, leasing inquiries, paper forms, or in-person interactions.</p>` +
        `<h1>Message Type and Frequency</h1>` +
        `<p>Users may receive messages related to leasing updates, appointment reminders, community announcements, and other property-related communications.</p>` +
        `<p><strong>Message frequency may vary</strong> depending on the user&rsquo;s engagement and communication preferences.</p>` +
        `<h1>Data Rates</h1>` +
        `<p>Standard message and data rates may apply. Users are responsible for any charges incurred from their mobile carrier.</p>` +
        `<h1>Customer Care</h1>` +
        `<p>Text &quot;HELP&quot; for help</p>` +
        `<h1>Opt-Out</h1>` +
        `<p>To stop receiving messages, reply &ldquo;STOP&rdquo; at any time to unsubscribe.</p>` +
        `<h1>Privacy</h1>` +
        `<p>Your information is handled in accordance with our <a href="/privacy-policy">Privacy Policy</a>.</p>`,
    },
  },
};

(TermsAndConditionsContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default TermsAndConditionsContent;
